"use client"

import { useMemo, useState, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import type { ToolComponentData } from "@/lib/tool-adapter"

// ---------- Decision logic ----------
// Industry baselines preserved verbatim from the legacy calculator:
//  • Average refrigerator repair cost: $350 (Consumer Reports survey data)
//  • Annual repair probability: 15% (Consumer Reports reliability data —
//    median major-repair rate across the first 5 service years)
//  • A break-even occurs when expected repair costs over the coverage
//    window meet or exceed the warranty price.

type Tone = "healthy" | "watch" | "replace"

interface Verdict {
  tone: Tone
  headline: string
  subline: string
  reasons: string[]
  costPerYear: number
  expectedRepairCost: number
  repairsToBreakEven: number
  costRatio: number | null
}

const AVG_REPAIR_COST = 350
const REPAIR_PROBABILITY_PER_YEAR = 0.15

function computeVerdict(cost: number, years: number, fridgePrice: number | null): Verdict {
  const costPerYear = cost / years
  const expectedRepairCost = AVG_REPAIR_COST * REPAIR_PROBABILITY_PER_YEAR * years
  const repairsToBreakEven = Math.ceil(cost / AVG_REPAIR_COST)
  const costRatio = fridgePrice && fridgePrice > 0 ? cost / fridgePrice : null

  let tone: Tone
  let headline: string
  let subline: string
  let reasons: string[]

  if (cost > expectedRepairCost * 1.5) {
    tone = "replace" // "Skip it" — same red tone, different label
    headline = "Skip it."
    subline = `Warranty ($${cost.toLocaleString()}) significantly exceeds expected repair costs ($${Math.round(expectedRepairCost)} over ${years} year${years > 1 ? "s" : ""}). The math doesn't work.`
    reasons = [
      `Costs $${costPerYear.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/year for ${years} year${years > 1 ? "s" : ""} of coverage.`,
      `Average refrigerator repair: $${AVG_REPAIR_COST}. You'd need ${repairsToBreakEven} repair${repairsToBreakEven > 1 ? "s" : ""} to break even.`,
      `Industry-average repair probability is 15% per year — expected total repairs over ${years} year${years > 1 ? "s" : ""} = $${Math.round(expectedRepairCost)}.`,
      "Self-insure: bank the warranty price and use it for any repair that comes up.",
    ]
  } else if (cost > expectedRepairCost) {
    tone = "watch"
    headline = "Borderline."
    subline = `Warranty ($${cost.toLocaleString()}) is slightly above expected repair costs ($${Math.round(expectedRepairCost)} over ${years} year${years > 1 ? "s" : ""}). Probably not worth it for the average buyer.`
    reasons = [
      `Costs $${costPerYear.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/year for ${years} year${years > 1 ? "s" : ""} of coverage.`,
      `Expected repair costs over ${years} year${years > 1 ? "s" : ""}: $${Math.round(expectedRepairCost)}.`,
      `You'd need ${repairsToBreakEven} repair${repairsToBreakEven > 1 ? "s" : ""} at $${AVG_REPAIR_COST} each to break even.`,
      "Worth it only if you value the peace-of-mind premium.",
    ]
  } else {
    tone = "healthy"
    headline = "Could pay off."
    subline = `Warranty ($${cost.toLocaleString()}) is below expected repair costs ($${Math.round(expectedRepairCost)} over ${years} year${years > 1 ? "s" : ""}). Math favors taking the coverage.`
    reasons = [
      `Costs $${costPerYear.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/year for ${years} year${years > 1 ? "s" : ""} of coverage.`,
      `Expected repair costs over ${years} year${years > 1 ? "s" : ""}: $${Math.round(expectedRepairCost)} — exceeds the warranty price.`,
      `You'd only need ${repairsToBreakEven} repair${repairsToBreakEven > 1 ? "s" : ""} at $${AVG_REPAIR_COST} each to break even.`,
      "Read the fine print: confirm the policy covers compressor and sealed-system repairs, not just cosmetic.",
    ]
  }

  if (costRatio !== null && costRatio > 0.15) {
    reasons.push(
      `Warranty is ${Math.round(costRatio * 100)}% of the fridge price — high relative to most extended-warranty norms (~10%).`,
    )
  }

  return {
    tone,
    headline,
    subline,
    reasons,
    costPerYear,
    expectedRepairCost,
    repairsToBreakEven,
    costRatio,
  }
}

const TONE_BORDER: Record<Tone, string> = {
  healthy: "#10a65c",
  watch: "#ff9951",
  replace: "#dc2626",
}
const TONE_BG: Record<Tone, string> = {
  healthy: "#ecfdf5",
  watch: "rgba(255,153,81,0.08)",
  replace: "#fef2f2",
}
const TONE_LABEL: Record<Tone, string> = {
  healthy: "Verdict · Could pay off",
  watch: "Verdict · Borderline",
  replace: "Verdict · Skip it",
}
const TONE_CTA: Record<Tone, { label: string; href: string }> = {
  healthy: { label: "Compare reliable models →", href: "/collections/refrigerators" },
  watch: { label: "See lifespan factors →", href: "/tools/refrigerator-lifespan-calculator" },
  replace: { label: "Find a longer-warranty model →", href: "/collections/refrigerators" },
}

const RELATED = [
  {
    title: "Repair or Replace",
    category: "Specialty",
    description: "Apply the 50% repair rule to any quote.",
    href: "/tools/repair-or-replace-refrigerator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Lifespan Calculator",
    category: "Specialty",
    description: "Years of life remaining on your fridge.",
    href: "/tools/refrigerator-lifespan-calculator",
    time: "~45 sec",
  },
  {
    title: "Energy Cost Calculator",
    category: "Energy & Cost",
    description: "Annual operating cost for any fridge.",
    href: "/tools/energy-cost-calculator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Age Calculator",
    category: "Specialty",
    description: "Decode the serial number to find the build year.",
    href: "/tools/refrigerator-age-calculator",
    time: "~30 sec",
  },
]

const FAQS = [
  {
    question: "Do extended warranties usually pay off?",
    answer:
      "For most refrigerator buyers, no. Independent consumer advocates have long advised against extended warranties on appliances — the typical buyer pays more in warranty premiums than they would in cumulative repairs. The exception is high-end built-in or commercial units where one sealed-system repair can exceed $1,000.",
  },
  {
    question: "What does the 15% repair probability come from?",
    answer:
      "Published reliability surveys put the median annual major-repair rate for refrigerators at roughly 12–18% across the first five service years. We use 15% as a midpoint baseline.",
  },
  {
    question: "Why $350 average repair cost?",
    answer:
      "Service-call surveys (including Angie's List) put the median fridge repair at $200–$500. We use $350 as a midpoint that reflects mixed parts/labor costs across major brands.",
  },
  {
    question: "Should I ever buy one?",
    answer:
      "Consider it if (1) the fridge is a high-end built-in or commercial unit with $1,000+ sealed-system parts, (2) the warranty includes compressor and sealed-system coverage, and (3) the annual cost is under 3% of the fridge price.",
  },
  {
    question: "What about the manufacturer's standard warranty?",
    answer:
      "Most new refrigerators ship with the maker's standard parts-and-labor warranty, and many carry longer coverage on the compressor or the sealed system (compressor, evaporator, condenser). The lengths differ by maker and model, so read the warranty statement for your model on the maker's own site. Don't pay extra for an extended warranty that just duplicates coverage you already have.",
  },
]

function InputLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
        {children}
      </label>
      {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
    </div>
  )
}

export function ExtendedWarrantyBreakEvenCalculatorClient(_props?: { toolData?: ToolComponentData }) {
  const [warrantyCost, setWarrantyCost] = useState<string>("250")
  const [coverageYears, setCoverageYears] = useState<string>("3")
  const [fridgePrice, setFridgePrice] = useState<string>("1500")

  const verdict = useMemo(() => {
    const cost = parseFloat(warrantyCost)
    const years = parseInt(coverageYears, 10)
    const price = parseFloat(fridgePrice)
    if (isNaN(cost) || isNaN(years) || cost <= 0 || years <= 0) return null
    return computeVerdict(cost, years, isNaN(price) ? null : price)
  }, [warrantyCost, coverageYears, fridgePrice])

  return (
    <ToolPageShell
      toolName="Extended Warranty Break-Even Calculator"
      heroEyebrow="Specialty"
      heroTitlePre="Is the extended warranty"
      heroTitleItalic="worth it?"
      heroSubtitle="Three numbers, one direct answer. We compare the warranty price against expected repair costs over the coverage window — using published repair-survey baselines."
      heroMeta={["⏱ ~30 sec", "Reliability + repair-cost survey data"]}
      heroShare={<ToolShareBar title="Is the extended warranty worth it?" />}
      faqs={FAQS}
      relatedTools={RELATED}
      cta={{
        title: "Skipping the warranty?",
        body: "Answer five questions to explore a starting product group, with prices where available. Verify dimensions, features and warranty terms on each product page.",
        buttonLabel: "Explore refrigerator groups",
        buttonHref: "/tools/find-your-fridge",
      }}
    >
      {/* ============ INPUTS + VERDICT ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Run the math
            </div>

            <div className="space-y-6">
              <div>
                <InputLabel hint="What the retailer is charging for the extended plan">
                  Warranty cost ($)
                </InputLabel>
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={warrantyCost}
                  onChange={(e) => setWarrantyCost(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>

              <div>
                <InputLabel hint="How many years the warranty extends past the manufacturer's coverage">
                  Coverage period (years)
                </InputLabel>
                <select
                  value={coverageYears}
                  onChange={(e) => setCoverageYears(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                >
                  <option value="1">1 year</option>
                  <option value="2">2 years</option>
                  <option value="3">3 years</option>
                  <option value="4">4 years</option>
                  <option value="5">5 years</option>
                </select>
              </div>

              <div>
                <InputLabel hint="Optional — gives us a cost-percent sanity check">
                  Fridge purchase price ($)
                </InputLabel>
                <input
                  type="number"
                  min={0}
                  step={50}
                  value={fridgePrice}
                  onChange={(e) => setFridgePrice(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
            </div>
          </div>

          {/* Verdict */}
          <div>
            {verdict && (
              <>
                <div
                  className="rounded-2xl p-6 sm:p-8 md:p-10 mb-6 md:mb-8"
                  style={{
                    background: TONE_BG[verdict.tone],
                    border: `1px solid ${TONE_BORDER[verdict.tone]}30`,
                  }}
                >
                  <div
                    className="text-[11px] font-bold uppercase tracking-[0.18em] mb-6 flex items-center gap-2"
                    style={{ color: TONE_BORDER[verdict.tone] }}
                  >
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background: TONE_BORDER[verdict.tone] }}
                    />
                    {TONE_LABEL[verdict.tone]}
                  </div>
                  <div
                    className="text-gray-900 font-black"
                    style={{
                      fontSize: "clamp(44px, 7vw, 80px)",
                      letterSpacing: "-0.05em",
                      lineHeight: 0.95,
                      textWrap: "balance" as never,
                    }}
                  >
                    {verdict.headline}
                  </div>
                  <p
                    className="mt-5 md:mt-6 text-gray-700"
                    style={{ fontSize: 17, lineHeight: 1.45, textWrap: "pretty" as never }}
                  >
                    {verdict.subline}
                  </p>
                </div>

                <div className="grid grid-cols-3 border-y border-gray-300 divide-x divide-gray-300 mb-6 md:mb-8">
                  <div className="py-5 pr-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Cost per year
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 30, letterSpacing: "-0.03em" }}
                    >
                      {`$${verdict.costPerYear.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                  <div className="py-5 px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Expected repairs
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 30, letterSpacing: "-0.03em" }}
                    >
                      {`$${Math.round(verdict.expectedRepairCost)}`}
                    </div>
                  </div>
                  <div className="py-5 pl-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Break even at
                    </div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 30,
                        letterSpacing: "-0.03em",
                        color: TONE_BORDER[verdict.tone],
                      }}
                    >
                      {verdict.repairsToBreakEven}
                      <span className="text-[13px] font-bold text-gray-500 ml-1">
                        repair{verdict.repairsToBreakEven !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                    Why this verdict
                  </div>
                  <ul className="space-y-2">
                    {verdict.reasons.map((r) => (
                      <li
                        key={r}
                        className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200"
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                          style={{ background: TONE_BORDER[verdict.tone] }}
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                        <p className="text-[14px] text-gray-700 leading-[1.55]">{r}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href={TONE_CTA[verdict.tone].href}
                  className="block h-14 rounded-lg text-[15px] font-extrabold tracking-tight text-white inline-flex items-center justify-center w-full"
                  style={{
                    background: verdict.tone === "replace" ? "#ff9951" : "#111827",
                    boxShadow:
                      verdict.tone === "replace" ? "0 8px 22px rgba(255,153,81,0.35)" : undefined,
                  }}
                >
                  {TONE_CTA[verdict.tone].label}
                </a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Build quality matters more than warranty"
          title="Counter-depth refrigerators to compare"
          description="Many refrigerators carry longer compressor coverage than the standard warranty — check yours before paying for a plan."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we decide" title="The methodology" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "Average repair: $350",
                d: "Service-call surveys (including Angie's List) put the median fridge repair at $200–$500. We use $350 as a midpoint across major brands.",
              },
              {
                n: "02",
                t: "Annual repair probability: 15%",
                d: "Published reliability surveys put the median annual major-repair rate for refrigerators at 12–18% in the first 5 service years. We use 15% as the baseline.",
              },
              {
                n: "03",
                t: "Break-even = warranty / repair cost",
                d: "We compute the number of average-cost repairs needed to recoup the warranty price, and compare it to the expected number of repairs over the coverage window.",
              },
            ].map((m) => (
              <div key={m.n}>
                <div className="font-mono text-[11px] font-bold tracking-[0.15em] mb-3 text-[#ff9951]">
                  {m.n}
                </div>
                <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-3 text-gray-900">
                  {m.t}
                </h3>
                <p className="text-[14px] text-gray-600 leading-[1.6]">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door fridges"
          description="Wider shelves, bottom-freezer access, the highest resale value."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
